import React, { useRef, useEffect, useState } from "react";
import * as echarts from "echarts";
import PropTypes from "prop-types";

const ViewStandarCpuRam = ({ alignment, data, dataDisponibilidad, onAlignmentChange }) => {
  const [transformedData, setTransformedData] = useState([]);
  const [dataFin, setDataFin] = React.useState(data || []);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  // Resize window height
  useEffect(() => {
    const handleResize = () => setWindowHeight(window.innerHeight);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Data transformation
  useEffect(() => {
    // console.log("Data desde ViewChart...", data);
    if (!data || !data.infoEc2 || !data.infoEc2.rows) {
      console.error("Data is missing or malformed");
      return;
    }

    const dataTransformada = data.infoEc2.rows.map((row) => ({
      time: parseInt(row.t) - 3600 * 5,
      value: parseFloat(row.c),
      valueRam: parseFloat(row.r),
    }));

    setDataFin(dataTransformada);
  }, [data]);

  return (
    <>
      {dataFin.length > 0 && (
        <RenderEchart
          vtDataRender={JSON.stringify(dataFin)}
          dataDisponibilidad={dataDisponibilidad}
          windowHeight={windowHeight}
        />
      )}
    </>
  );
};

ViewStandarCpuRam.propTypes = {
  alignment: PropTypes.string.isRequired,
  dataDisponibilidad: PropTypes.number,
  data: PropTypes.shape({
    infoEc2: PropTypes.shape({
      rows: PropTypes.arrayOf(
        PropTypes.shape({
          t: PropTypes.string.isRequired,
          c: PropTypes.string.isRequired,
          r: PropTypes.string.isRequired,
        })
      ),
    }),
  }).isRequired,
  onAlignmentChange: PropTypes.func.isRequired,
};

// -------------------------
// Componente RenderEchart
// -------------------------
function RenderEchart({ vtDataRender, dataDisponibilidad, windowHeight }) {
  const chartRef = useRef(null);
  const parsedData = JSON.parse(vtDataRender);
  const days = [];
  const cpuArray = [];
  const ramArray = [];

  const formatTimestamp = (unixTime) => {
    const date = new Date((unixTime + 3600 * 5) * 1000); // Ajuste a UTC-5
    return date.toLocaleDateString("es-ES", {
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  parsedData.forEach((item) => {
    days.push(formatTimestamp(item.time));
    cpuArray.push(item.value);
    ramArray.push(item.valueRam);
  });

  useEffect(() => {
    if (!chartRef.current) return;

    const chart = echarts.init(chartRef.current);

    chart.setOption({
      title: {
        text: `Informe Disponibilidad - ${dataDisponibilidad?.toFixed(2) ?? "0.00"}%`,
        left: "center",
      },
      tooltip: {
        trigger: "axis",
      },
      legend: {
        data: ["Cpu", "Ram"],
        left: 10,
      },
      toolbox: {
        feature: {
          dataZoom: { yAxisIndex: "none" },
          dataView: { readOnly: false },
          magicType: { type: ["line", "bar"] },
          restore: {},
          saveAsImage: {},
        },
      },
      axisPointer: { link: [{ xAxisIndex: "all" }] },
      dataZoom: [
        {
          show: true,
          realtime: true,
          start: 0,
          end: 100,
          xAxisIndex: [0, 1],
        },
        {
          type: "inside",
          realtime: true,
          start: 0,
          end: 100,
          xAxisIndex: [0, 1],
        },
      ],
      grid: [
        { left: 80, right: 80, height: "30%" },
        { left: 80, right: 80, top: "60%", height: "30%" },
      ],
      xAxis: [
        {
          type: "category",
          boundaryGap: false,
          data: days,
        },
        {
          gridIndex: 1,
          type: "category",
          boundaryGap: false,
          data: days,
        },
      ],
      yAxis: [
        {
          name: "Métrica",
          type: "value",
          max: 100,
        },
        {
          gridIndex: 1,
          name: "Métrica",
          type: "value",
          max: 100,
        },
      ],
      series: [
        {
          name: "Cpu",
          type: "line",
          symbolSize: 6,
          data: cpuArray,
          markPoint: {
            data: [
              { type: "max", name: "Max" },
              { type: "min", name: "Min" },
            ],
          },
          markLine: {
            data: [
              [
                { symbol: "none", yAxis: 90, x: "5%" },
                {
                  symbol: "none",
                  yAxis: 90,
                  x: "95%",
                  label: {
                    position: "insideEndTop",
                    formatter: "Rango 90%",
                    color: "#000",
                  },
                },
              ],
            ],
            lineStyle: {
              color: "red",
              width: 1,
              type: "dashed",
            },
          },
        },
        {
          name: "Ram",
          type: "line",
          xAxisIndex: 1,
          yAxisIndex: 1,
          symbolSize: 6,
          data: ramArray,
          markPoint: {
            data: [
              { type: "max", name: "Max" },
              { type: "min", name: "Min" },
            ],
          },
          markLine: {
            data: [
              [
                { symbol: "none", yAxis: 90, x: "5%" },
                {
                  symbol: "none",
                  yAxis: 90,
                  x: "95%",
                  label: {
                    position: "insideEndTop",
                    formatter: "Rango 90%",
                    color: "#000",
                  },
                },
              ],
            ],
            lineStyle: {
              color: "red",
              width: 1,
              type: "dashed",
            },
          },
        },
      ],
    });

    return () => {
      chart.dispose();
    };
  }, [vtDataRender, dataDisponibilidad, windowHeight]);

  return (
    <div
      ref={chartRef}
      style={{
        width: "100%",
        height: `${Math.min(windowHeight - 200)}px`,
        minHeight: "300px",
      }}
    />
  );
}

RenderEchart.propTypes = {
  vtDataRender: PropTypes.string.isRequired,
  dataDisponibilidad: PropTypes.number,
  windowHeight: PropTypes.number.isRequired,
};

export default ViewStandarCpuRam;
